import React, { useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from "@mui/material";
import CareScreenerBackground from "@components/care-screener/CareScreenerBackground";
import Flex from "@components/core/Flex";
import AuthService from "../services/AuthService";

export default function Unsubscribe() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleUnsubscribe = async () => {
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await AuthService.unsubscribeFromEmails(email.trim());
      setSuccess(true);
    } catch (err) {
      console.error("Error unsubscribing:", err);
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <CareScreenerBackground>
        <Flex>
          <Card sx={{ maxWidth: 500, width: "100%" }}>
            <CardContent sx={{ padding: 4 }}>
              <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600, textAlign: "center" }}>
                Successfully Unsubscribed
              </Typography>

              <Typography variant="body1" sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}>
                You have been successfully unsubscribed from Manatee marketing and outreach emails.
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  padding: 2,
                  borderRadius: 1,
                  textAlign: "center"
                }}
              >
                <Typography variant="body2">You can close this window.</Typography>
              </Box>
            </CardContent>
          </Card>
        </Flex>
      </CareScreenerBackground>
    );
  }

  return (
    <CareScreenerBackground>
      <Flex>
        <Card sx={{ maxWidth: 500, width: "100%" }}>
          <CardContent sx={{ padding: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600, textAlign: "center" }}>
              Unsubscribe from Emails
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}>
              Enter your email address to unsubscribe from Manatee marketing and outreach emails.
            </Typography>

            {error && (
              <Box
                sx={{
                  backgroundColor: "#ffebee",
                  color: "#c62828",
                  padding: 2,
                  borderRadius: 1,
                  mb: 3,
                  textAlign: "center"
                }}
              >
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={handleUnsubscribe}
                disabled={loading || !email.trim()}
                sx={{
                  minWidth: 180,
                  position: "relative"
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ position: "absolute" }} /> : "Unsubscribe"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Flex>
    </CareScreenerBackground>
  );
}
